/**
 * Segment slides: every SEGMENT (alcove, door, window, opening) slides
 * parallel along its host line, or resizes either end, as one `slide`
 * gesture. Host- and segment-type-agnostic through LineEdgeRef; only the
 * clamps differ. An alcove on a primary edge respects alcove constraints;
 * everything else packs against its line-segment neighbors.
 */

import {
  alcovePositionRange,
  alcoveWidthRange,
  LineFrame,
  lineSegPositionRange,
  lineSegWidthRange,
  Point,
  resolveLineRef,
  setAlcoveLeftWall,
  setAlcovePosition,
  setAlcoveWidth,
  setSegmentPosition,
  setSegmentStart,
  setSegmentWidth,
} from '@kaiizen/kai-floor-plan';
import { dot2d } from '../../math2d';
import type { EditorMutableState, Gesture, LineEdgeRef, Selection } from '../editorState';
import { select } from '../selection';
import { beginDraft } from './draft';

type SlideGesture = Extract<Gesture, { kind: 'slide' }>;

/** How far along a line a point sits, measured from the line's start. */
const distanceAlong = (lineFrame: LineFrame, point: Point): number => dot2d({ x: point.x - lineFrame.start.x, y: point.y - lineFrame.start.y }, lineFrame.dir);

/** Slide a segment along its host line, or resize one of its ends. */
export function beginSlideDrag(
  state: EditorMutableState,
  ref: LineEdgeRef,
  segmentIndex: number,
  mode: 'move' | 'start' | 'end',
  model: Point,
  /** Selection to set (undefined = select the segment itself; null = keep current). */
  selection?: Selection | null,
): void {
  const line = resolveLineRef(state.shape, ref);
  const segment = line?.segments[segmentIndex];
  if (!line || !segment) return;
  if (selection !== null) select(state, selection ?? { kind: 'lineSeg', ref, si: segmentIndex });
  const { lf: lineFrame, segments, minBound, maxBound } = line;
  const isAlcove = segment.edgeSegmentType === 'alcove' && ref.k === 'pe';
  const positionRange = isAlcove
    ? alcovePositionRange(state.shape, ref.edge, segmentIndex)
    : lineSegPositionRange(segments, segmentIndex, lineFrame.length, minBound, maxBound);
  const widthRange = isAlcove
    ? alcoveWidthRange(state.shape, ref.edge, segmentIndex)
    : lineSegWidthRange(segments, segmentIndex, lineFrame.length, maxBound);
  const pointerAlong = distanceAlong(lineFrame, model);

  let range: { min: number; max: number };
  let grabOffset = 0;
  let anchor = 0;
  if (mode === 'move') {
    range = positionRange;
    grabOffset = pointerAlong - segment.position;
  } else if (mode === 'start') {
    anchor = segment.position + segment.width;
    range = { min: positionRange.min, max: anchor - widthRange.min };
  } else {
    range = widthRange;
  }
  beginDraft(state);
  state.drag = { kind: 'slide', ref, si: segmentIndex, mode, range, grabOffset, anchor };
}

/** Advance a slide: move the segment with the pointer, or move the end being resized to it. */
export function moveSegmentSlide(state: EditorMutableState, gesture: SlideGesture, model: Point): void {
  const base = state.shape;
  const line = resolveLineRef(base, gesture.ref);
  const segment = line?.segments[gesture.si];
  if (!line || !segment) return;
  const alcoveEdge = gesture.ref.k === 'pe' && segment.edgeSegmentType === 'alcove' ? gesture.ref.edge : null;
  const pointerAlong = distanceAlong(line.lf, model);
  if (gesture.mode === 'move') {
    const position = pointerAlong - gesture.grabOffset;
    state.draftShape = alcoveEdge
      ? setAlcovePosition(base, alcoveEdge, gesture.si, position)
      : setSegmentPosition(base, gesture.ref, gesture.si, position);
  } else if (gesture.mode === 'start') {
    state.draftShape = alcoveEdge
      ? setAlcoveLeftWall(base, alcoveEdge, gesture.si, pointerAlong)
      : setSegmentStart(base, gesture.ref, gesture.si, pointerAlong - line.minBound);
  } else {
    const width = pointerAlong - segment.position;
    state.draftShape = alcoveEdge
      ? setAlcoveWidth(base, alcoveEdge, gesture.si, width)
      : setSegmentWidth(base, gesture.ref, gesture.si, width);
  }
}
